/* global NodeJS */
import type { Ref } from 'vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { isCancel } from 'axios';
import _isEqual from 'lodash/isEqual';

import { useLoaderStore } from '@/stores/useLoaderStore';
import { useUserStore } from '@/stores/useUserStore';
import { fetchGetDashboardTracking } from '@/helpers/fetchers';
import {
  closeTrackingRefreshNotification,
  showTrackingRefreshNotification,
} from '@/helpers/notifiers';
import * as routeNames from '@/constants/routeNames';

export interface PrimaryTrackingDashboardViewDataComposable {
  isFetchingAllData: Ref<boolean>;
  isFetchingDashboardJobs: Ref<boolean>;
  allData: Ref<
    | {
        dashboardJobs?: openapi.DashboardJobsTrackingContentOpenapi;
      }
    | null
    | undefined
  >;
  refreshDashboardJobs: () => Promise<void>;
}

export default function usePrimaryTrackingDashboardViewData(
  queryDate: Readonly<Ref<Date | undefined>>,
): PrimaryTrackingDashboardViewDataComposable {
  const route = useRoute();
  const userStore = useUserStore();

  const loaderStore = useLoaderStore();

  let dashboardJobsController: AbortController | undefined;
  let timeout: number | NodeJS.Timeout | undefined;
  const userTimeZone = computed(() => userStore.user.timezone);

  const allDataFetchCounter = ref(0);
  const dashboardJobsFetchCounter = ref(0);
  const isFetchingAllData = computed(() => !!allDataFetchCounter.value);
  const isFetchingDashboardJobs = computed(
    () => !!dashboardJobsFetchCounter.value,
  );
  const fetchedDashboardJobsData =
    ref<openapi.DashboardJobsTrackingContentOpenapi>();
  const allData = ref<{
    dashboardJobs?: openapi.DashboardJobsTrackingContentOpenapi;
  } | null>();

  function clear(): void {
    closeTrackingRefreshNotification();
    clearTimeout(timeout);
    if (dashboardJobsController) dashboardJobsController.abort();
  }

  onUnmounted(() => {
    clear();
  });

  async function _fetchDashboardJobs(): Promise<void> {
    closeTrackingRefreshNotification();
    clearTimeout(timeout);
    if (dashboardJobsController) dashboardJobsController.abort();
    const date = queryDate.value;
    if (!date) {
      fetchedDashboardJobsData.value = undefined;
      return;
    }
    dashboardJobsController = new AbortController();
    await fetchGetDashboardTracking(userTimeZone.value, date, {
      signal: dashboardJobsController.signal,
    })
      .then((data) => {
        fetchedDashboardJobsData.value = data;
        timeout = setTimeout(
          function () {
            if (route.name === routeNames.JOBS) {
              showTrackingRefreshNotification(() => {
                void refreshDashboardJobs();
              });
            } else if (route.name === routeNames.JOBS_TV) {
              void refreshDashboardJobs();
            }
          },
          5 * 60 * 1000, // 5min
        );
      })
      .catch((err: unknown) => {
        if (!isCancel(err)) {
          fetchedDashboardJobsData.value = undefined;
          throw err;
        }
      });
  }

  function updateResults(): void {
    if (!fetchedDashboardJobsData.value) {
      allData.value = null;
      return;
    }
    allData.value = {
      dashboardJobs: fetchedDashboardJobsData.value,
    };
  }

  async function refreshDashboardJobs(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    dashboardJobsFetchCounter.value++;
    try {
      await _fetchDashboardJobs();
    } finally {
      updateResults();
      dashboardJobsFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  watch(
    [queryDate],
    async (newValues, oldValues) => {
      if (
        !(route.name === routeNames.JOBS || route.name === routeNames.JOBS_TV)
      ) {
        return;
      }
      if (_isEqual(newValues, oldValues)) return;
      loaderStore.increaseGlobalLoader();
      allDataFetchCounter.value++;
      dashboardJobsFetchCounter.value++;
      try {
        await _fetchDashboardJobs();
      } finally {
        updateResults();
        dashboardJobsFetchCounter.value--;
        allDataFetchCounter.value--;
        loaderStore.decreaseGlobalLoader();
      }
    },
    { immediate: true },
  );

  return {
    isFetchingAllData,
    isFetchingDashboardJobs,
    allData,
    refreshDashboardJobs,
  };
}
